// assets/product-price-discount.js

/**
 * Product Price Discount Handler — Club Olay + OrderGroove Integration
 * 
 * Handles Club Olay member discount across product size selection and OrderGroove subscription options.
 * 
 * BEHAVIOR:
 * 1. When user clicks a size variant button (.pss__btn):
 *    - Updates price from button's .pss__price-sale (Club Olay discounted price from Liquid)
 *    - Updates compare price from button's .pss__price-compare (original variant price)
 *    - Updates price-per-oz unit pricing
 *    - After 1 second, automatically triggers click on active OrderGroove card if present
 *      (ensures OrderGroove subscription discount is applied on top of variant change)
 * 
 * 2. When user clicks OrderGroove card (.og-card--subscribe or .og-card--onetime):
 *    - Reads price from OrderGroove shadow DOM (og-price[subscription] or og-price[regular])
 *    - Applies Club Olay discount based on metafield discount_type (percentage|fixed-amount)
 *    - Updates .product-price--original with final discounted price
 * 
 * DISCOUNT LOGIC:
 * - If .product-price--root has 'club-olay-active' class → Liquid pre-calculated Club discount
 * - JavaScript reads discounted price and applies OrderGroove subscription discount if active
 * - Prevents double-application by using Liquid's pre-discounted prices when available
 * 
 * UPDATES:
 * - .product-price--original (final discounted price)
 * - .product-price--compare (original variant price with strikethrough)
 * - .price-per-oz (unit price from selected variant)
 * 
 * IMPORTANT: This script only loads for logged-in Club Olay members
 * See layout/theme.liquid - wrapped in {%- if customer -%} condition
 */

(function() {
  'use strict';

  // Module-level variables to store last applied Club Olay discount
  // These persist across function calls to re-apply after theme updates
  let lastDiscountedPrice = null;
  let lastComparePrice = null;

  // Parse money string to number (handles $XX.XX, $X,XXX.XX, etc.)
  const parseMoney = (moneyString) => {
    if (!moneyString) return 0;
    // Remove currency symbols, commas, and whitespace
    const cleanedString = moneyString.replace(/[$,\s]/g, '');
    const parsed = parseFloat(cleanedString);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Format number as money string (e.g., 24.99 → "$24.99")
  const formatMoney = (amount) => {
    return '$' + amount.toFixed(2);
  };

  /**
   * Re-apply Club Olay discount after theme's ProductPrice element updates
   * This prevents the theme from overwriting our discounted price with raw variant price
   */
  const reapplyClubDiscount = () => {    
    if (lastDiscountedPrice === null) {
      return;
    }

    const originalPriceEl = document.querySelector('.product-price--original');

    const comparePriceEl = document.querySelector('.product-price--compare');

    if (!originalPriceEl) {
      console.warn('[Re-apply] Cannot find .product-price--original element');
      return;
    }
    // Re-apply stored Club Olay discounted price
    originalPriceEl.innerHTML = formatMoney(lastDiscountedPrice);
    originalPriceEl.setAttribute('aria-label', `Price: ${formatMoney(lastDiscountedPrice)}`);

    // Re-apply compare price if available
    if (comparePriceEl && lastComparePrice !== null) {
      comparePriceEl.innerHTML = '<s>' + formatMoney(lastComparePrice) + '</s>';
      comparePriceEl.setAttribute('aria-label', `Regular price: ${formatMoney(lastComparePrice)}`);
      comparePriceEl.style.display = 'block';
      comparePriceEl.style.visibility = 'visible';
    }
  };

  // Handle size selector button click
  const handleSizeButtonClick = (event) => {
    const btn = event.target.closest('.pss__btn');
    if (!btn) return;

    // Ignore disabled buttons - early return
    if (btn.classList.contains('pss__btn--disabled')) {
      event.preventDefault();
      event.stopImmediatePropagation(); // Stop ALL handlers including inline scripts
      return;
    }

    // Ignore already selected buttons - prevent ALL event handlers
    if (btn.classList.contains('pss__btn--selected')) {
      event.preventDefault();
      event.stopImmediatePropagation(); // Stop ALL handlers including inline scripts
      return;
    }

    // Step 2: Get the sale price from within the clicked button
    const salePriceElement = btn.querySelector('.pss__price-sale');
    if (!salePriceElement) {
      console.warn('No .pss__price-sale found in clicked button');
      return;
    }

    const salePriceText = salePriceElement.innerHTML.trim();
    const salePriceValue = parseMoney(salePriceText);

    if (salePriceValue === 0) {
      console.warn('Could not parse sale price from:', salePriceText);
      return;
    }

    // Step 2b: Get the compare price (original variant price before discount) from the clicked button
    const comparePriceElement = btn.querySelector('.pss__price-compare');
    let originalVariantPrice = salePriceValue; // Fallback to sale price if no compare price
    
    if (comparePriceElement) {
      // Extract text from <s> tag if present, otherwise use innerHTML directly
      const comparePriceText = comparePriceElement.textContent || comparePriceElement.innerHTML.trim();
      const parsedComparePrice = parseMoney(comparePriceText);
      
      if (parsedComparePrice > 0) {
        originalVariantPrice = parsedComparePrice;
      } else {
        console.warn('Could not parse compare price from:', comparePriceText, '- using sale price as fallback');
      }
    } else {
      console.warn('No .pss__price-compare found in clicked button - using sale price as compare price');
    }

    // Check if Club Olay discount already applied by Liquid
    const priceRoot = document.querySelector('.product-price--root');
    const isClubAlreadyActive = priceRoot && priceRoot.classList.contains('club-olay-active');

    let displayPrice = salePriceValue;

    if (isClubAlreadyActive) {
      // Liquid already calculated discount - use sale price as-is
      displayPrice = salePriceValue;     
    } else {
      // No Club discount active - sale price is the display price
      displayPrice = salePriceValue;
    }

    // Step 3: Update the main product price elements
    const originalPriceEl = document.querySelector('.product-price--original');
    const comparePriceEl = document.querySelector('.product-price--compare');

    if (!originalPriceEl) {
      console.error('Cannot find .product-price--original element');
      return;
    }

    // Store the discounted price for re-application after theme updates
    lastDiscountedPrice = displayPrice;
    lastComparePrice = originalVariantPrice;
    // Update original price (discounted or regular price)
    originalPriceEl.innerHTML = formatMoney(displayPrice);
    originalPriceEl.setAttribute('aria-label', `Price: ${formatMoney(displayPrice)}`);   

    // Update compare price (original variant price BEFORE discount from pss__price-compare)
    if (comparePriceEl) {
      comparePriceEl.innerHTML = '<s>' + formatMoney(originalVariantPrice) + '</s>';
      comparePriceEl.setAttribute('aria-label', `Regular price: ${formatMoney(originalVariantPrice)}`);
      // Force visibility - override CSS rule that hides empty elements
      comparePriceEl.style.display = 'block';
      comparePriceEl.style.visibility = 'visible';
      
      // Also ensure parent wrapper is visible (in case it has data-block-type)
      const priceWrapper = comparePriceEl.closest('[data-block-type="price"]');
      if (priceWrapper) {
        priceWrapper.style.display = '';
      }
      
    } else {
      console.warn('Cannot find .product-price--compare element - skipping compare price update');
    }

    const btnPricePerOz = btn.querySelector('.price-per-oz');
    const mainPricePerOz = document.querySelector('.product-price--wrapper .price-per-oz');
    
    if (btnPricePerOz && mainPricePerOz) {
      const pricePerOzText = btnPricePerOz.innerHTML.trim();
      mainPricePerOz.innerHTML = pricePerOzText;
    }
    // Updated unit value price-per-unit if present



    // After PSS price update, trigger OrderGroove card click if one is active
    // This ensures OrderGroove subscription/onetime discount logic runs
    setTimeout(() => {
      // Check which OrderGroove card is active
      const subscribeCardActive = document.querySelector('.og-card--subscribe.sub-active');
      const onetimeCardActive = document.querySelector('.og-card--onetime.one-active');

      let activeCard = null;
      let cardType = '';

      if (subscribeCardActive) {
        activeCard = subscribeCardActive;
        cardType = 'Subscribe & Save';
      } else if (onetimeCardActive) {
        activeCard = onetimeCardActive;
        cardType = 'One-Time Purchase';
      }

      if (activeCard) {      
        // Programmatically click the active card to trigger handleSubscribeCardClick
        activeCard.click();
      }
    }, 1000);
  };

  // Common function to apply Club Olay discount
  const applyClubDiscount = (basePrice, logPrefix = '') => {
    // CRITICAL CHECK: Only apply discount if product has club-olay-active class
    // This class is added by Liquid when product has discount metafields configured
    const productPriceRoot = document.querySelector('.product-price--root');
    const hasClubOlayActive = productPriceRoot && productPriceRoot.classList.contains('club-olay-active');
    
    if (!hasClubOlayActive) {
      return basePrice;
    }
    
    const discountTypeEl = document.querySelector('.club_olay_discount_type');
    const discountValueEl = document.querySelector('.club_olay_discount_value');

    if (!discountTypeEl || !discountValueEl) {
      console.warn(`${logPrefix}Club Olay discount inputs not found - no discount applied`);
      return basePrice;
    }

    const discountType = discountTypeEl.value.trim();
    const discountValue = parseFloat(discountValueEl.value);

    if (!discountType || isNaN(discountValue) || discountValue <= 0) {
      console.warn(`${logPrefix}Invalid discount values - Type:`, discountType, 'Value:', discountValue);
      return basePrice;
    }

    let finalPrice = basePrice;
    const discountTypeNormalized = discountType.toLowerCase().trim();

    if (discountTypeNormalized === 'fixed-amount') {
      // Subtract fixed dollar amount
      finalPrice = basePrice - discountValue;
    } else if (discountTypeNormalized === 'percentage') {
      // Apply percentage discount
      const discountAmount = basePrice * (discountValue / 100);
      finalPrice = basePrice - discountAmount;
    } else {
      console.warn(`${logPrefix}Unknown discount type:`, discountType, '- no discount applied');
      return basePrice;
    }

    // Ensure price doesn't go negative
    finalPrice = Math.max(0, finalPrice);
    return finalPrice;
  };

  // Handle Subscribe & Save and One-Time Purchase card clicks
  const handleSubscribeCardClick = (event) => {
    if(document.querySelector(".og-block-active")){
    // Get the clicked element and its classList dynamically
    const clickedElement = event.target;
    
    // Find the card - either subscribe or onetime
    const card = clickedElement.closest('.og-card');
    
    if (!card) return;

    // Apply discount logic - check immediately and after delay for class toggle
    const applySubscribeDiscount = () => {
      let ogPriceValue = null;
      let cardType = '';
      let priceSource = '';

      // Determine which card type and state
      const isSubscribeCard = card.classList.contains('og-card--subscribe');
      const isOnetimeCard = card.classList.contains('og-card--onetime');
      const hasSubActive = card.classList.contains('sub-active');
      const hasOneActive = card.classList.contains('one-active');
      // Check which card state is active
      if (isSubscribeCard && hasSubActive) {
        // Subscribe & Save is active
        cardType = 'Subscribe & Save';
        priceSource = 'subscriptionPrice';

      const ogPriceData_sub = document.querySelector('og-price[subscription]').__discountedProductPriceFromOffers.subscriptionPrice;
      const og_regular = document.querySelector('og-price[subscription]').__discountedProductPriceFromOffers.regularPrice;
      
      if (!ogPriceData_sub || !og_regular) {
          console.warn('[Card Click] Cannot find subscription or regular price data');
          return;
        }
      
      // Parse prices to numbers
      const subPrice = parseMoney(ogPriceData_sub);
      const regularPrice = parseMoney(og_regular);
      
      // Calculate actual discount percentage between subscription and regular price
      const actualDiscountPercentage = ((regularPrice - subPrice) / regularPrice) * 100;
      
      // Fetch shop-level expected OrderGroove discount percentage from hidden input
      const ogDiscountInput = document.querySelector('.og_discount_percentage_calculation_hidden');
      const expectedDiscountPercentage = ogDiscountInput ? parseFloat(ogDiscountInput.value) : 5;
      
      // Check if actual discount matches expected (within 0.5% tolerance)
      if (Math.abs(actualDiscountPercentage - expectedDiscountPercentage) < 0.5) {
        // Discount already applied correctly - use subscription price as-is
        ogPriceValue = subPrice;
      } else {
        // Discount not applied or incorrect - calculate it manually
        const discountMultiplier = (100 - expectedDiscountPercentage) / 100;
        ogPriceValue = regularPrice * discountMultiplier;
      }

      } else if (isOnetimeCard && hasOneActive) {
        // One-time purchase is active
        cardType = 'One-Time Purchase';
        priceSource = 'regularPrice';

        const ogPriceData_reg = document.querySelector('og-price[regular]').__discountedProductPriceFromOffers.regularPrice;        

        ogPriceValue = parseMoney(ogPriceData_reg);

      } else {
        // Not in active state - skip
        return;
      }

      if (ogPriceValue === 0) {
        console.warn('[Card Click] Could not parse price - value is 0');
        return;
      }

      // Apply Club Olay discount using common function
      const finalPrice = applyClubDiscount(ogPriceValue, '[Card Click] ');

      // Store for re-application after theme updates
      lastDiscountedPrice = finalPrice;


      // Update product-price--original
      const originalPriceEl = document.querySelector('.product-price--original');
      if (!originalPriceEl) {
        return;
      }
      originalPriceEl.innerHTML = formatMoney(finalPrice);
      originalPriceEl.setAttribute('aria-label', `Price: ${formatMoney(finalPrice)}`);

      // *** CRITICAL FIX: Update price-per-oz.price--unit-value when og-card is clicked ***
      // Find the specific element that needs updating
      const pricePerOzElement = document.querySelector('.price-per-oz.price--unit-value');
      
      if (!pricePerOzElement) {
        console.warn('[Price/Oz] Cannot find .price-per-oz.price--unit-value element');
      } else {
        // Get unit value and type from the selected size button
        const selectedSizeBtn = document.querySelector('.pss__btn--selected');
        
        if (selectedSizeBtn) {
          // Size button has .price-per-oz.price-per-unit with data-unit-value
          const btnPricePerOz = selectedSizeBtn.querySelector('.price-per-oz.price-per-unit');
          
          if (btnPricePerOz) {
            // data-unit-value contains the weight/unit value for that variant (e.g., "2" for 2oz)
            const unitValue = parseFloat(btnPricePerOz.getAttribute('data-unit-value'));
            // data-type on the main element contains the unit type (e.g., "oz")
            const unitType = pricePerOzElement.getAttribute('data-type') || 'oz';
            if (unitValue && unitValue > 0) {
              // Calculate new price per unit
              const pricePerUnit = finalPrice / unitValue;
              const pricePerUnitFormatted = pricePerUnit.toFixed(2);
              // Update the display element
              pricePerOzElement.innerHTML = `($${pricePerUnitFormatted}/${unitType})`;
            } else {
              console.warn('[Price/Oz] Invalid unit value:', unitValue);
            }
          } else {
            console.warn('[Price/Oz] No .price-per-oz.price-per-unit element found in selected size button');
          }
        } else {
          const unitType = pricePerOzElement.getAttribute('data-type') || 'oz';
          let _hdnVolume = document.querySelector('.hdnVolume').value;
          if(_hdnVolume){
              const pricePerUnit = finalPrice / _hdnVolume ;
              const pricePerUnitFormatted = pricePerUnit.toFixed(2);
              pricePerOzElement.innerHTML = `($${pricePerUnitFormatted}/${unitType})`;
          }
          else {
            pricePerOzElement.innerHTML = '';
          }
        }
      }
    };

    // Try immediately (if sub-active already present)
    applySubscribeDiscount();

    // Also try after 100ms delay (in case sub-active class is added by other script)
    setTimeout(applySubscribeDiscount, 100);
  }
  };

  // Initialize event delegation when DOM is ready
  const init = () => {
    // Event delegation for size selector buttons
    // Use capture phase (true) to run BEFORE inline button handlers
    document.addEventListener('click', handleSizeButtonClick, true);
    
    // Event delegation specifically for .og-card clicks
    document.addEventListener('click', (event) => {
      const ogCard = event.target.closest('.og-card');

    // CRITICAL FIX: Listen for theme's variant change events and re-apply Club Olay discount
    // ProductPrice element in theme-async.js overwrites our discount, so we intercept and restore it
    const productPriceEl = document.querySelector('product-price-element');
    if (productPriceEl && productPriceEl.dataset.id) {
      const productId = productPriceEl.dataset.id;
      
      // Use window.on() which is the theme's custom event system
      if (typeof window.on === 'function') {
        window.on(`theme:product:${productId}:variantChanged.ClubOlayDiscount`, () => {
          // Small delay to ensure ProductPrice has finished updating
          setTimeout(reapplyClubDiscount, 50);
        });
      } else {
        console.warn('[Init] window.on() not available - Club Olay discount may be overwritten by theme');
      }
    } else {
      console.warn('[Init] product-price-element not found - Club Olay discount may be overwritten');
    }
      if (ogCard) {
        handleSubscribeCardClick(event);
      }
    });

    // Run subscription discount check after 2 seconds on page load
    // Delay ensures OrderGroove and other third-party scripts have initialized
    setTimeout(() => {
      handleSubscribeCardClick({ target: document.querySelector('.og-card--subscribe.sub-active, .og-card--onetime.one-active') });
    }, 2000);
  };

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();